import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {Blockquote} from "@wfp/ui";
import {KVPair, ListPage, Order, ServerError, Sortable} from "../../models/types";
import {getOrders} from "../../restapi/order";
import {updateSorting} from "../../restapi/restapi";
import {useInViewport} from "../../hooks/useInViewport";
import {OrderCard} from "./card";
import {OrderFilters} from "./filters";
import {OrderTable} from "./table";

const SortInfo: Sortable[] = [
    {code: 'id', field: 'id', enabled: true, mode: 0},
    {code: 'order_id', field: 'id', enabled: true, mode: 0},
    {code: 'delivery_date', field: 'delivery_date', enabled: true, mode: 0},
    {code: 'distribution_date', field: 'distribution_date', enabled: true, mode: 0},
    {code: 'last_modified', field: 'last_modified', enabled: true, mode: 0},
    {code: 'partner', field: 'partner_name', enabled: true, mode: 0},
    {code: 'supplier', field: 'supplier_name', enabled: true, mode: 0},
    {code: 'state', field: 'state', enabled: true, mode: 0},
];

interface Props {
    page?: ListPage<Order>,
    mobile?: boolean,
    filters?: KVPair[],
    disableFilter?: boolean,
    sorting?: string[];
    onClick?: (id: number) => void
    onSelectOrder?: (order: Order) => void
}

export const OrderList = (props: Props) => {
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState<ListPage<Order>>();
    const [error, setError] = useState<ServerError>();
    const {isInViewport, ref} = useInViewport();
    const {t} = useTranslation();
    const navigate = useNavigate();

    // Derive sortInfo with current mode from page.sorting
    const currentSortInfo = SortInfo.map(sortable => {
        if (!page?.sorting) return sortable;

        const field = sortable.field;
        const sortEntry = page.sorting.find(s =>
            s === field || s === `+${field}` || s === `-${field}`
        );

        if (!sortEntry) return { ...sortable, mode: 0 };  // No sort
        if (sortEntry.startsWith('-')) return { ...sortable, mode: 2 };  // DESC
        if (sortEntry === field || sortEntry.startsWith('+')) return { ...sortable, mode: 1 };  // ASC
        return sortable;
    });

    useEffect(() => {
        if (!!props.page) {
            setPage(props.page);
        } else {
            loadPage(1, undefined, props.filters, props.sorting);
        }
    }, [props.page]);

    function loadPage(pageNumber = 1, pageSize?: number, options?: KVPair[], sorting?: string[]): void {
        setLoading(true);
        setError(undefined);
        getOrders(pageNumber, pageSize, options, sorting)
            .then((res) => {
                if (props.mobile && !!page) {
                    res.results = (page.results || []).concat(res.results);
                }
                setPage(res);
            })
            .catch(error => setError(error))
            .finally(() => setLoading(false));
    }

    function handleSorting(field: string): void {
        const value = updateSorting(field, page?.sorting || []);
        loadPage(page?.pageNumber, page?.pageSize, page?.filtering, value);
    }

    function handleFiltering(filters: KVPair[]): void {
        const changed = filters.length !== page?.filtering?.length || filters.filter(kvp => !page?.filtering?.find(f => f.key === kvp.key && f.value === kvp.value)).length > 0
        if (changed) {
            loadPage(page?.pageNumber || 1, page?.pageSize, filters, page?.sorting);
        }
    }

    function handleClickOrder(id: number): void {
        if (!!props.onClick) {
            props.onClick(id);
        } else {
            navigate(`/orders/${id}`);
        }
    }

    function handleLoadMore(): boolean {
        if (!!page) {
            const result = !loading && !!page.next;
            if (result) {
                loadPage(page.pageNumber + 1, page.pageSize, page?.filtering, page?.sorting);
            }
            return result;
        }
        return false;
    }

    return (
        <div>
            {!!error?.detail && (
                <Blockquote kind="error" withIcon>
                    {error.detail.toString()}
                </Blockquote>
            )}
            {!!page && (
                <div className="mx-0">
                    {page?.count === 0 && (
                        <Blockquote kind="warning" withIcon>
                            {t("empty_orders_list")}
                        </Blockquote>
                    )}
                    {!!props.mobile && page?.results.map((order: Order) => (
                        <OrderCard key={order.id} item={order} onClick={() => handleClickOrder(order.id)}/>
                    ))}
                    {!!props.mobile && (
                        <div ref={ref}>{isInViewport && handleLoadMore() && (<div>Loading.....</div>)}</div>
                    )}
                    {!props.mobile && !!page && (
                        <>
                        {!props.disableFilter && (
                            <OrderFilters onSubmit={handleFiltering}/>
                        )}
                            <OrderTable page={page} loadPage={loadPage}
                                        onSelectOrder={props.onSelectOrder}
                                        onClick={handleClickOrder}
                                        hidePagination={page.count <= page.results.length}
                                        sort={handleSorting}
                                        sortInfo={currentSortInfo}
                            />
                        </>

                    )}
                </div>
            )}
        </div>
    );
}
